import React, {useCallback, useState} from 'react';
import {useMounted} from '../../../../../hooks/useMounted';
import {useDispatch} from 'react-redux';
import {modelRetire} from '../../../../../store/model';
import {trackModelRetired} from '../../../../../tracking/tracking';
import {messageAdd, MessageTypes} from '../../../../../store/message';
import {Button, Checkbox, Input, Typography} from 'antd';
import {PoweroffOutlined} from '@ant-design/icons';
import {tid} from '../../../../../testUtils';
import {ModelActionProps} from '../../../actionMenuTypes';

const {Text, Title} = Typography;
const headingTitleStyle = {margin: '8px 0 1rem 0', fontSize: '2.2rem', lineHeight: '1.6rem', fontWeight: 700 as const};
const headingSubtitleStyle = {fontSize: '2rem', lineHeight: '2.6rem', fontWeight: 400 as const};

const ModelRetire: React.FC<ModelActionProps> = ({model, onSuccess, onCancel}) => {
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);
  const [reason, setReason] = useState('');
  const [agree, setAgree] = useState(false);
  const mounted = useMounted();

  const valid = agree && !!reason.trim();

  const handleSubmit = useCallback(() => {
    setSubmitting(true);
    modelRetire(dispatch, model.uuid, reason.trim())
      .then(() => {
        trackModelRetired(model);
        dispatch(messageAdd('Model has been retired.', MessageTypes.SUCCESS));
        onSuccess?.();
      })
      .catch(() => {
        dispatch(messageAdd('Failed to retire model', MessageTypes.ERROR));
      })
      .finally(() => {
        mounted.current && setSubmitting(false);
      });
  }, [dispatch, model, reason, onSuccess, mounted]);

  return (
    <>
      <div style={{display: 'flex', gap: '1rem', alignItems: 'flex-start'}}>
        {/* Left: Icon */}
        <div style={{flexShrink: 0}}>
          <PoweroffOutlined style={{color: '#ff4d4f', fontSize: '3.2rem'}} />
        </div>

        {/* Right: Content */}
        <div style={{flex: 1}}>
          {/* Title */}
          <Title level={5} style={headingTitleStyle}>
            Retire Model Entry
          </Title>

          {/* Subtitle */}
          <div style={{marginBottom: '1.2rem'}}>
            <Text style={headingSubtitleStyle}>This operation can&apos;t be undone!</Text>
          </div>

          <div style={{marginBottom: '1.2rem'}}>
            <Text strong>Reason</Text>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              status={reason.trim() ? undefined : 'error'}
              {...tid('input-reason')}
            />
          </div>

          <div style={{marginBottom: '1.6rem', display: 'flex', alignItems: 'flex-start', gap: '8px'}}>
            <Checkbox
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
              style={{marginTop: '2px', flexShrink: 0}}
            />
            <span onClick={() => setAgree(!agree)} style={{cursor: 'pointer', userSelect: 'none'}}>
              I confirm that all files in this model entry will be retired and will not be available for users of Model
              Repository and third parties using Model Repository entries. It will be visible only to Editors, flagged
              as retired.
            </span>
          </div>

          <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: '2.4rem', gap: '0.8rem'}}>
            <Button onClick={onCancel} {...tid('dialog-retire-btn-cancel')}>
              Cancel
            </Button>
            <Button
              type="primary"
              danger
              onClick={handleSubmit}
              loading={submitting}
              disabled={!valid}
              icon={<PoweroffOutlined />}
              {...tid('dialog-retire-btn-submit')}
            >
              Retire
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ModelRetire;
